import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  AttachFile,
  MoreVert,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import AppointmentDetailView from "./appointmentDetailView";
import { useNavigate } from "react-router-dom";
import ChatModal from "../chatModal";
import { DateTime } from "luxon";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";
import { supabase } from "../../../supabaseClient";
import { canJoinMeetingNow, formatMeetingWindowLocal, formatCountdown, formatMeetingDetails } from '../../../helpers/stringHelper';
import { authenticatedFetch } from "../../../network/api";

function getLocalTimeAgo(utcTimestamp) {
  return DateTime.fromISO(utcTimestamp, { zone: "utc" })
    .setZone(Intl.DateTimeFormat().resolvedOptions().timeZone) // converts to user's local TZ
    .toRelative(); // e.g., "4 minutes ago"
}

// Updated AppointmentRow component
const AppointmentRow = ({
  appointment,
  onViewDetail,

  formatDate,
  formatTime,
  getTimeAgo,
  intakeStatus,
  hasForm,
}) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [intakeData, setIntakeData] = useState(null);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [joinCountdown, setJoinCountdown] = useState(0);
  const [joinWindowText, setJoinWindowText] = useState("");
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();
  function openChat(user) {
    setChatUser(user);
    setChatOpen(true);
  }

  function closeChat() {
    setChatOpen(false);
    setChatUser(null);
  }

  const openIntake = async () => {
    setIntakeOpen(true);
    setIntakeData(null);

    try {
      const res = await fetch(
        `/api/attorney/webapp/intake/forms/view?appointmentId=${appointment.id}`
      );
      const { answers, urls, schema, error: apiError } = await res.json();

      if (!res.ok || apiError) {
        throw new Error(apiError || "Failed to load intake");
      }

      // on success we only store answers, urls and schema
      setIntakeData({ answers, urls, schema });
    } catch (err) {
      console.error("openIntake error:", err);
      setIntakeData({ error: err.message });
    }
  };
  const closeIntake = () => setIntakeOpen(false);

  // Countdown effect
  useEffect(() => {
    let timer;
    if (joinModalOpen && joinCountdown > 0) {
      timer = setInterval(() => {
        setJoinCountdown((prev) => (prev > 1000 ? prev - 1000 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [joinModalOpen, joinCountdown]);

  const lawyer = useSelector((s) => s.attorneyData.attorneyData);
  const videoToken = useSelector((s) => s.attorneyData.videoToken);

  const handleJoin = (appt) => {
    const startISO = appt.requestedTimestamp || appt.appointment_time;
    const endISO = appt.end_ts;
    const { allowed, countdown, start, end } = canJoinMeetingNow(startISO, endISO);
    if (!allowed) {
      const meetingDetails = formatMeetingDetails(start, end);
      setJoinWindowText(meetingDetails.fullText);
      setJoinCountdown(countdown > 0 ? countdown : 0);
      setJoinModalOpen(true);
      return;
    }
    const url = new URL("/lawyer-video", window.location.origin);
    url.searchParams.append("token", JSON.stringify(videoToken));
    url.searchParams.append("userId", JSON.stringify(lawyer.user_id));
    url.searchParams.append("callId", JSON.stringify(appt.id));
    url.searchParams.append("name", JSON.stringify(lawyer.name));
    window.open(url, "_blank");
  };

  // Render intake status chip
  const renderIntakeStatusChip = () => {
    if (intakeStatus?.status === "submitted") {
      return (
        <Chip
          label="✓ Submitted"
          sx={{
            bgcolor: "#e8f5e8",
            color: "#2e7d32",
            fontWeight: 500,
            fontSize: "12px",
            height: "24px",
          }}
        />
      );
    } else if (intakeStatus?.status === "created") {
      return (
        <Chip
          label="⏳ Not Sent"
          sx={{
            bgcolor: "#fff3e0",
            color: "#f57c00",
            fontWeight: 500,
            fontSize: "12px",
            height: "24px",
          }}
        />
      );
    } else {
      return (
        <Chip
          label="❌ Not filled"
          sx={{
            bgcolor: "#ffebee",
            color: "#d32f2f",
            fontWeight: 500,
            fontSize: "12px",
            height: "24px",
          }}
        />
      );
    }
  };

  return (
    <>
      {/* Main row */}
      <TableRow hover>
        {/* 1) Client Name */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#144a8c",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {appointment.client?.first_name?.[0]}
              {appointment.client?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600} color="#1a1a1a">
                {appointment.client?.first_name} {appointment.client?.last_name}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* 2) Submitted updated */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Typography variant="body2" color="#666">
            {appointment.created_at
              ? getLocalTimeAgo(appointment.created_at)
              : "N/A"}
          </Typography>
        </TableCell>

        {/* 3) Appointment Time */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box>
            <Typography variant="body1" fontWeight={600} color="#1a1a1a">
              {formatDate
                ? formatDate(appointment.requested_slot?.date)
                : "No Date found"}
            </Typography>
            <Typography variant="body2" color="#666">
              {formatTime
                ? formatTime(appointment.requested_slot?.time)
                : "No Time found"}{" "}
              CST
            </Typography>
          </Box>
        </TableCell>

        {/* 4) Consultation Type */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Chip
            label={
              appointment.appointmentType?.appointment_name ||
              "No Appointment Name found"
            }
            sx={{
              bgcolor: "#f5f5f5",
              color: "#333",
              fontWeight: 500,
              fontSize: "14px",
              height: "32px",
            }}
          />
        </TableCell>

        {/* 5) Intake Form Status */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          {renderIntakeStatusChip()}
        </TableCell>

        {/* 6) Files */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AttachFile sx={{ fontSize: 18, color: "#666" }} />
            <Typography variant="body2" color="#666" fontWeight={500}>
              {appointment.documents?.length || 0}
            </Typography>
          </Box>
        </TableCell>

        {/* 7) Action */}
        <TableCell sx={{ borderBottom: "none", py: 2 }}>
          <MoreVert />
        </TableCell>
      </TableRow>

      {/* AI Recommendation Alert (full-width) */}
      {/* <TableRow>
        <TableCell colSpan={7} sx={{ borderBottom: "none", p: 0 }}>
          <Alert
            severity="info"
            sx={{
              bgcolor: "#e3f2fd",
              border: "1px solid #bbdefb",
              borderRadius: "8px",
              m: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#1976d2"
                sx={{ mb: 0.5 }}
              >
                ⚡ AI suggests:{" "}
                {appointment.ai_recommendation ||
                  "No AI recommendation available"}
              </Typography>
              <Typography variant="body2" color="#1976d2">
                {appointment.ai_reason ||
                  "No reason provided for AI recommendation."}
              </Typography>
            </Box>
          </Alert>
        </TableCell>
      </TableRow> */}

      <TableRow>
        <TableCell colSpan={7} sx={{ borderBottom: "1px solid #e0e0e0", p: 0 }}>
          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "flex-end", p: 2 }}
          >
            {intakeStatus?.status === "submitted" && (
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#144a8c",
                  color: "white",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  "&:hover": {
                    bgcolor: "#144a7a",
                  },
                }}
                onClick={openIntake}
              >
                View Intake
              </Button>
            )}

            {intakeStatus?.status !== "submitted" && (
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#144a8c",
                  color: "#144a8c",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  "&:hover": { bgcolor: "#f5f9ff", borderColor: "#0d3a6c" },
                }}
                disabled={resending}
                onClick={async () => {
                  try {
                    setResending(true);
                    const r = await authenticatedFetch('/api/attorney/webapp/intake/forms/resend', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ appointmentId: appointment.id }),
                    });
                    if (r.status === 429) {
                      const j = await r.json();
                      alert(`Intake recently sent. Try again in ~${j.retryAfterMinutes} minutes.`);
                    } else if (r.status === 409) {
                      // already submitted
                    } else if (!r.ok) {
                      alert('Failed to resend intake.');
                    }
                  } finally {
                    setResending(false);
                  }
                }}
              >
                {resending ? 'Sending…' : 'Resend Intake'}
              </Button>
            )}

            <Button
              variant="contained"
              sx={{
                bgcolor: "#144a8c",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  bgcolor: "#144a7a",
                },
              }}
              onClick={() => openChat(appointment.client)}
            >
              Chat
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#144a8c",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  bgcolor: "#144a7a",
                },
              }}
              onClick={() => handleJoin(appointment)}
            >
              Join Meeting
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#144a8c",
                color: "#144a8c",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#0d3a6c",
                  bgcolor: "#f5f9ff",
                },
              }}
              onClick={() => navigate(`/attorney/case/${appointment.id}`)}
            >
              Open Case
            </Button>
            {!hasForm && (
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#4caf50",
                  color: "white",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 2.5,
                  py: 0.75,
                  "&:hover": {
                    bgcolor: "#45a049",
                  },
                }}
                // onClick={() =>
                //   navigate(`/attorney/intake/create/${appointment.id}`, {
                //     state: { appointment },
                //   })
                // }
              >
                Create Intake
              </Button>
            )}
            <Button
              variant="outlined"
              sx={{
                borderColor: "#ddd",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f9f9f9",
                },
              }}
              // onClick={}
            >
              Reschedule
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#ddd",
                color: "#666",
                textTransform: "none",
                fontWeight: 500,
                px: 2.5,
                py: 0.75,
                "&:hover": {
                  borderColor: "#bbb",
                  bgcolor: "#f9f9f9",
                },
              }}
              onClick={() => onViewDetail && onViewDetail(appointment)}
            >
              View Detail
            </Button>
          </Box>
        </TableCell>
      </TableRow>
      <ChatModal open={chatOpen} onClose={closeChat} clientUser={chatUser} />

      <Dialog open={intakeOpen} onClose={closeIntake} maxWidth="sm" fullWidth>
        <DialogTitle>Intake Response</DialogTitle>
        <DialogContent dividers>
          {!intakeData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : intakeData.error ? (
            <Typography color="error">{intakeData.error}</Typography>
          ) : (
            // iterate the saved schema so you show fields in order with their labels
            intakeData.schema.fields.map(({ id, label }) => {
              const answer = intakeData.answers[id];
              const url = intakeData.urls[id];

              return (
                <Box key={id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {label}
                  </Typography>

                  {url ? (
                    <Link href={url} target="_blank" rel="noopener">
                      {answer.filename || url}
                    </Link>
                  ) : (
                    <Typography>
                      {answer != null ? answer.toString() : "—"}
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIntake}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={joinModalOpen} 
        onClose={() => setJoinModalOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Poppins, sans-serif',
          }
        }}
      >
        <DialogTitle 
          style={{ 
            backgroundColor: '#144A8C', 
            color: 'white', 
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: '20px',
            textAlign: 'center',
            padding: '24px 24px 16px 24px'
          }}
        >
          ⏰ Meeting Not Yet Available
        </DialogTitle>
        <DialogContent 
          style={{ 
            padding: '32px 24px 24px 24px',
            fontFamily: 'Poppins, sans-serif'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ 
              fontSize: '16px', 
              color: '#666', 
              marginBottom: '16px',
              fontWeight: 500
            }}>
              This meeting is scheduled for:
            </div>
            <div style={{ 
              color: '#144A8C', 
              fontWeight: 600, 
              fontSize: '18px', 
              marginBottom: '24px',
              lineHeight: '1.4',
              backgroundColor: '#f8f9ff',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #e3e8ff'
            }}>
              {joinWindowText}
            </div>
            {joinCountdown > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                gap: '12px',
                backgroundColor: '#fff3e0',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #ffe0b2'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#f57c00', 
                  fontWeight: 500
                }}>
                  You can join in:
                </div>
                <div style={{ 
                  color: '#f57c00', 
                  fontWeight: 700, 
                  fontSize: '28px',
                  fontFamily: 'monospace',
                  letterSpacing: '2px'
                }}>
                  {formatCountdown(joinCountdown)}
                </div>
              </div>
            ) : (
              <div style={{ 
                backgroundColor: '#ffebee',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid #ffcdd2'
              }}>
                <div style={{ 
                  color: '#d32f2f', 
                  fontWeight: 500,
                  textAlign: 'center'
                }}>
                  The meeting is not currently accessible.
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions 
          style={{ 
            padding: '0 24px 24px 24px',
            justifyContent: 'center'
          }}
        >
          <Button 
            onClick={() => setJoinModalOpen(false)}
            variant="contained"
            style={{
              backgroundColor: '#144A8C',
              color: 'white',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '10px 32px',
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: '#0d3a6b'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentRow;
